import React from "react"
import { Redirect } from "react-router-dom"
import { useContentfulEntry } from "#/lib/contentful/hooks/useContentfulEntry"
import { TypePageSkeleton } from "#/types/contentful"
import { ContentfulContentRenderer } from "#/services/contentful"
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageHeader } from "#/features/global-ui/page-header"
import { Title } from "#/services/page-title/Title"
import { Paths } from "#/utils/Paths"
import { FullScreenLoading } from "#/components/read/shared/FullScreenLoading"
import { useConditionDepsStatus } from "#/services/contentful/hooks/use-contentful-conditions"
import { useDoesUserSatisfyConditions } from "#/services/contentful/conditional-gate"
import { toCondition } from "#/lib/contentful/utils/conditionUtils"
import { usePageViewTracking } from "#/services/analytics/usePageViewTracking"

interface ContentfulPageProps {
  entryId: string
  flagEnabled: boolean
  conditionsNotMetRedirectPath?: string
  pageViewTracking?: {
    isLoading: boolean
    metadata: Record<string, unknown>
  }
}

export function ContentfulPage({
  entryId,
  flagEnabled,
  conditionsNotMetRedirectPath = Paths.HOME,
  pageViewTracking
}: ContentfulPageProps): JSX.Element {
  const { data: page, isLoading: isPageLoading } = useContentfulEntry<TypePageSkeleton>(entryId)
  const { isLoading: areConditionDepsLoading } = useConditionDepsStatus()
  const aggregateIsLoading = isPageLoading || areConditionDepsLoading || (pageViewTracking?.isLoading ?? false)
  const { doesUserSatisfyConditions } = useDoesUserSatisfyConditions()

  const conditions = (page?.fields?.conditions ?? []).map(toCondition).filter(Boolean)
  const userSatisfiesConditions = doesUserSatisfyConditions(conditions)
  const shouldRenderContent = flagEnabled && !aggregateIsLoading && userSatisfiesConditions
  usePageViewTracking({
    enabled: shouldRenderContent,
    metadata: pageViewTracking?.metadata
  })

  if (!flagEnabled) {
    return <Redirect to={conditionsNotMetRedirectPath} />
  }

  if (aggregateIsLoading) {
    return <FullScreenLoading />
  }

  if (!userSatisfiesConditions) {
    return <Redirect to={conditionsNotMetRedirectPath} />
  }

  return (
    <>
      <Title title={page?.fields?.navigationText} />
      <PageLayout variant="sidebar" header={<PageHeader pageTitle={page?.fields?.navigationText} />}>
        <ContentfulContentRenderer contents={page?.fields?.content} />
      </PageLayout>
    </>
  )
}
